import { Injectable } from '@angular/core';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { GridApi } from 'ag-grid-community';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ExportService {
  $exportDataToFormat: Subject<string> = new Subject<string>();
  $reportExportFormatTypeChange: Subject<string> = new Subject<string>();

  constructor() { }

  exportDataToFormat(format: string, fileName: string, exportType: 'TABLE' | 'CHART' | 'BOTH'): void {
    this.$exportDataToFormat.next(JSON.stringify({
      format,
      fileName,
      exportType
    }));
  }

  public exportToFormat(
    format: string,
    gridApi: GridApi | null,
    reportVisualType: string,
    fileName?: string,
    exportType?: 'TABLE' | 'CHART' | 'BOTH',
    agGridChartComponent?: any
  ): void {
    if (!format) {
      console.error('Export format not specified');
      return;
    }
  
    const fileNameToUse = fileName || 'report_data';
    const upperFormat = format.toUpperCase();
    
    if (exportType !== 'CHART' && !gridApi) {
      console.error('Grid API not available for export');
      alert('Export failed: Data grid is not ready');
      return;
    }

    if (exportType === 'CHART' || (reportVisualType === 'CHART' && !exportType)) {
      const supportedChartFormats = ['PNG', 'JPG', 'PDF'];
      if (!supportedChartFormats.includes(upperFormat)) {
        console.error(`Unsupported chart export format: ${format}`);
        alert(`Export failed: ${format} is not supported for chart export. Please use PNG, JPG or PDF.`);
        return;
      }

      if (!agGridChartComponent) {
        console.error('AgGridChartComponent not provided for chart export');
        alert('Export failed: Chart component not available');
        return;
      }

      this.exportAgGridChartToImage(upperFormat, fileNameToUse, 2, agGridChartComponent, gridApi!);
      return;
    }

    if (exportType === 'BOTH') {
      if (!agGridChartComponent || !gridApi) {
        console.error('Grid API and chart component required for BOTH export');
        alert('Export failed: Chart and table data not available');
        return;
      }

      if (upperFormat === 'PDF') {
        this.exportBothToPDF(gridApi, agGridChartComponent, fileNameToUse, 2);
      } else if (upperFormat === 'PNG' || upperFormat === 'JPG') {
        this.exportBothToImage(gridApi, agGridChartComponent, fileNameToUse, upperFormat);
      } else {
        console.warn(`Format ${format} not supported for BOTH export`);
        alert(`Export failed: ${format} is not supported for combined export. Please use PDF for best results.`);
      }
      return;
    }
    
    switch (upperFormat) {
      case 'PDF':
        this.exportToPDF(gridApi!, fileNameToUse);
        break;
      case 'XLSX':
      case 'XLS':
        this.exportToExcel(gridApi!, format.toLowerCase() as 'xlsx' | 'xls', fileNameToUse);
        break;
      case 'CSV':
        this.exportToCSV(gridApi!, fileNameToUse);
        break;
      default:
        console.error(`Unsupported export format: ${format}`);
        alert(`Export failed: ${format} is not a supported export format`);
    }
  }

  private exportToPDF(gridApi: GridApi, fileName: string = 'report_data'): void {
    try {
      const doc = new jsPDF();
      
      const title = fileName;
      doc.text(title, 14, 16);
      
      const columnDefs = gridApi.getColumnDefs() as any[];
      if (!columnDefs || columnDefs.length === 0) {
        throw new Error('Grid columns not available for export');
      }
      
      const visibleColumns = columnDefs
        .filter(col => !col.hide)
        .map(col => ({
          title: col.headerName || col.field,
          dataKey: col.field
        }));
      
      const rowData: any[] = [];
      gridApi.forEachNodeAfterFilterAndSort((node) => {
        rowData.push(node.data);
      });
      
      const tableData = rowData.map(row => {
        return visibleColumns.map(col => {
          const value = row[col.dataKey];
          return value !== undefined && value !== null ? value : '';
        });
      });
      
      autoTable(doc, {
        head: [visibleColumns.map(col => col.title)],
        body: tableData,
        startY: 20,
        margin: { top: 20 },
        styles: { overflow: 'linebreak' },
        headStyles: { fillColor: [75, 75, 75] }
      });
      
      // Save the PDF with the custom filename
      doc.save(`${fileName}.pdf`);
    } catch (error) {
      console.error('PDF export error:', error);
      alert('PDF export failed. Please try a different export format.');
    }
  }

  private exportToCSV(gridApi: GridApi, fileName: string = 'report_data'): void {
    const params = {
      fileName: `${fileName}.csv`,
      onlySelected: false
    };
    
    gridApi.exportDataAsCsv(params);
  }

  private exportToExcel(gridApi: GridApi, type: 'xlsx' | 'xls', fileName: string = 'report_data'): void {
    try {
      // Get visible columns
      const columnDefs = gridApi.getColumnDefs() as any[];
      if (!columnDefs || columnDefs.length === 0) {
        throw new Error('Grid columns not available for export');
      }
      
      const visibleColumns = columnDefs
        .filter(col => !col.hide)
        .map(col => ({
          header: col.headerName || col.field,
          field: col.field
        }));
      
      // Get all rows (after filtering and sorting)
      const rowData: any[] = [];
      gridApi.forEachNodeAfterFilterAndSort((node) => {
        rowData.push(node.data);
      });
      
      // Create CSV content with proper Excel escaping
      let csvContent = 'data:text/csv;charset=utf-8,';
      
      csvContent += visibleColumns.map(col => `"${col.header}"`).join(',') + '\r\n';
      
      // Add data rows
      rowData.forEach(row => {
        const rowValues = visibleColumns.map(col => {
          const value = row[col.field] !== undefined && row[col.field] !== null ? row[col.field] : '';
          return `"${String(value).replace(/"/g, '""')}"`;
        });
        csvContent += rowValues.join(',') + '\r\n';
      });
      
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement('a');
      link.setAttribute('href', encodedUri);
      link.setAttribute('download', `report_data.${type}`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Excel export error:', error);
      alert('Excel export failed. Please try a different export format.');
    }
  }

  private async exportAgGridChartToImage(
    format: string,
    fileName: string,
    scale: number,
    agGridChartComponent: any,
    gridApi: GridApi
  ): Promise<void> {
    try {
      const chartId = agGridChartComponent.getChartId();

      if (!chartId) {
        throw new Error('Chart ID not found');
      }

      const fileFormat = format === 'PNG' ? 'image/png' : 'image/jpeg';

      try {
        const dataUrl = gridApi.getChartImageDataURL({
          chartId: chartId,
          fileFormat: fileFormat
        });

        if (dataUrl) {
          console.log('Using native AG Grid export for', format);

          if (format === 'PDF') {
            const canvas = await this.dataUrlToCanvas(dataUrl);
            this.downloadCanvasAsPDF(canvas, fileName);
            return;
          }

          this.downloadFromDataUrl(dataUrl, fileName, format);
          return;
        }
      } catch (error) {
        console.warn('Native AG Grid export failed, falling back to html2canvas:', error);
      }

      console.log('Falling back to html2canvas for', format);
      const chartContainer = agGridChartComponent.getChartContainer();
      if (!chartContainer) {
        throw new Error('Chart container not found');
      }

      const chartWrapper = chartContainer.closest('.ag-chart-wrapper');
      const menuButtons = chartWrapper?.querySelectorAll('.ag-chart-menu');
      menuButtons?.forEach((btn: any) => btn.style.display = 'none');

      const canvas = await html2canvas(chartContainer, {
        scale: scale,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        ignoreElements: (element) => {
          return element.classList.contains('ag-chart-menu');
        }
      });

      menuButtons?.forEach((btn: any) => btn.style.display = '');

      if (format === 'PNG' || format === 'JPG') {
        this.downloadCanvasAsImage(canvas, fileName, format);
      } else if (format === 'PDF') {
        this.downloadCanvasAsPDF(canvas, fileName);
      }

    } catch (error) {
      console.error('Error exporting chart:', error);
      alert(`Failed to export chart: ${error}`);
    }
  }

  private downloadFromDataUrl(dataUrl: string, fileName: string, format: string): void {
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = `${fileName}.${format.toLowerCase()}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private downloadCanvasAsImage(canvas: HTMLCanvasElement, fileName: string, format: string): void {
    const mimeType = format === 'PNG' ? 'image/png' : 'image/jpeg';
    canvas.toBlob((blob) => {
      if (blob) {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${fileName}.${format.toLowerCase()}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    }, mimeType, 0.95);
  }

  private downloadCanvasAsPDF(canvas: HTMLCanvasElement, fileName: string): void {
    const pdf = new jsPDF({
      orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
      unit: 'px',
      format: [canvas.width, canvas.height]
    });

    const imgData = canvas.toDataURL('image/png');
    pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
    pdf.save(`${fileName}.pdf`);
  }

  private async exportBothToPDF(
    gridApi: GridApi,
    agGridChartComponent: any,
    fileName: string,
    scale: number
  ): Promise<void> {
    try {
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const availableWidth = pageWidth - (margin * 2);

      const columnDefs = gridApi.getColumnDefs() || [];
      const rowData: any[] = [];
      gridApi.forEachNodeAfterFilterAndSort((node: any) => {
        rowData.push(node.data);
      });

      const columns = columnDefs
        .filter((col: any) => col.field && !col.hide)
        .map((col: any) => ({
          header: col.headerName || col.field,
          dataKey: col.field
        }));

      autoTable(pdf, {
        head: [columns.map((col: any) => col.header)],
        body: rowData.map((row: any) =>
          columns.map((col: any) => {
            const value = row[col.dataKey];
            return value !== null && value !== undefined ? String(value) : '';
          })
        ),
        startY: margin,
        margin: { left: margin, right: margin },
        styles: { fontSize: 8 },
        headStyles: { fillColor: [15, 73, 160] },
      });

      const finalY = (pdf as any).lastAutoTable.finalY || margin;

      const chartId = agGridChartComponent.getChartId();
      if (!chartId) {
        throw new Error('Chart ID not found');
      }

      let chartCanvas: HTMLCanvasElement;

      try {
        const dataUrl = gridApi.getChartImageDataURL({
          chartId: chartId,
          fileFormat: 'image/png'
        });

        if (dataUrl) {
          console.log('Using native AG Grid export for chart in BOTH PDF');
          chartCanvas = await this.dataUrlToCanvas(dataUrl);
        } else {
          throw new Error('Native chart export returned no data');
        }
      } catch (error) {
        console.error('Native chart export failed:', error);
        throw new Error('Chart export failed. Please try exporting table and chart separately.');
      }

      const chartAspect = chartCanvas.width / chartCanvas.height;
      const chartWidth = availableWidth;
      const chartHeight = chartWidth / chartAspect;
      const chartY = finalY + 10;

      if (chartY + chartHeight > pageHeight - margin) {
        pdf.addPage();
        const chartImgData = chartCanvas.toDataURL('image/png');
        pdf.addImage(chartImgData, 'PNG', margin, margin, chartWidth, chartHeight);
      } else {
        const chartImgData = chartCanvas.toDataURL('image/png');
        pdf.addImage(chartImgData, 'PNG', margin, chartY, chartWidth, chartHeight);
      }

      pdf.save(`${fileName}.pdf`);
    } catch (error) {
      console.error('Error exporting both to PDF:', error);
      alert(`Failed to export: ${error}`);
    }
  }

  private async exportBothToImage(
    gridApi: GridApi,
    agGridChartComponent: any,
    fileName: string,
    format: string
  ): Promise<void> {
    try {
      const chartId = agGridChartComponent.getChartId();
      if (!chartId) {
        throw new Error('Chart ID not found');
      }

      const chartDataUrl = gridApi.getChartImageDataURL({
        chartId: chartId,
        fileFormat: format === 'PNG' ? 'image/png' : 'image/jpeg'
      });

      if (!chartDataUrl) {
        throw new Error('Failed to export chart');
      }

      const chartCanvas = await this.dataUrlToCanvas(chartDataUrl);
      const tableCanvas = await this.renderTableToCanvas(gridApi);

      const combinedCanvas = document.createElement('canvas');
      const padding = 40;
      const margin = 20;

      const maxWidth = Math.max(tableCanvas.width, chartCanvas.width);
      combinedCanvas.width = maxWidth + (margin * 2);
      combinedCanvas.height = tableCanvas.height + chartCanvas.height + padding + (margin * 2);

      const ctx = combinedCanvas.getContext('2d');
      if (!ctx) {
        throw new Error('Failed to get canvas context');
      }

      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, combinedCanvas.width, combinedCanvas.height);

      const tableX = margin + (maxWidth - tableCanvas.width) / 2;
      ctx.drawImage(tableCanvas, tableX, margin);

      const chartX = margin + (maxWidth - chartCanvas.width) / 2;
      const chartY = margin + tableCanvas.height + padding;
      ctx.drawImage(chartCanvas, chartX, chartY);

      this.downloadCanvasAsImage(combinedCanvas, fileName, format);

    } catch (error) {
      console.error('Error exporting both to image:', error);
      alert(`Failed to export: ${error}`);
    }
  }

  private async renderTableToCanvas(gridApi: GridApi): Promise<HTMLCanvasElement> {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }

    const columnDefs = gridApi.getColumnDefs() || [];
    const visibleColumns = columnDefs.filter((col: any) => col.field && !col.hide);

    const rowData: any[] = [];
    gridApi.forEachNodeAfterFilterAndSort((node: any) => {
      rowData.push(node.data);
    });

    const cellPadding = 10;
    const headerHeight = 40;
    const rowHeight = 30;
    const fontSize = 12;
    const headerFontSize = 14;

    const columnWidths = visibleColumns.map((col: any) => {
      const headerText = col.headerName || col.field;
      ctx.font = `bold ${headerFontSize}px Arial`;
      const headerWidth = ctx.measureText(headerText).width + (cellPadding * 2);

      let maxDataWidth = headerWidth;
      rowData.slice(0, 10).forEach((row: any) => {
        const value = row[col.field];
        const text = value !== null && value !== undefined ? String(value) : '';
        ctx.font = `${fontSize}px Arial`;
        const textWidth = ctx.measureText(text).width + (cellPadding * 2);
        maxDataWidth = Math.max(maxDataWidth, textWidth);
      });

      return Math.max(100, Math.min(maxDataWidth, 300));
    });

    const tableWidth = columnWidths.reduce((sum, width) => sum + width, 0);
    const tableHeight = headerHeight + (rowData.length * rowHeight);

    canvas.width = tableWidth;
    canvas.height = tableHeight;

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, tableWidth, tableHeight);

    ctx.fillStyle = '#0F49A0';
    ctx.fillRect(0, 0, tableWidth, headerHeight);

    ctx.fillStyle = '#ffffff';
    ctx.font = `bold ${headerFontSize}px Arial`;
    ctx.textBaseline = 'middle';

    let xOffset = 0;
    visibleColumns.forEach((col: any, colIndex: number) => {
      const headerText = col.headerName || col.field;
      ctx.fillText(headerText, xOffset + cellPadding, headerHeight / 2);

      if (colIndex < visibleColumns.length - 1) {
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(xOffset + columnWidths[colIndex], 0);
        ctx.lineTo(xOffset + columnWidths[colIndex], headerHeight);
        ctx.stroke();
      }

      xOffset += columnWidths[colIndex];
    });

    ctx.fillStyle = '#000000';
    ctx.font = `${fontSize}px Arial`;

    rowData.forEach((row: any, rowIndex: number) => {
      const yPos = headerHeight + (rowIndex * rowHeight);

      if (rowIndex % 2 === 1) {
        ctx.fillStyle = '#f9f9f9';
        ctx.fillRect(0, yPos, tableWidth, rowHeight);
        ctx.fillStyle = '#000000';
      }

      xOffset = 0;
      visibleColumns.forEach((col: any, colIndex: number) => {
        const value = row[col.field];
        const text = value !== null && value !== undefined ? String(value) : '';

        let displayText = text;
        const maxWidth = columnWidths[colIndex] - (cellPadding * 2);
        let textWidth = ctx.measureText(displayText).width;

        if (textWidth > maxWidth) {
          while (textWidth > maxWidth && displayText.length > 0) {
            displayText = displayText.slice(0, -1);
            textWidth = ctx.measureText(displayText + '...').width;
          }
          displayText += '...';
        }

        ctx.fillText(displayText, xOffset + cellPadding, yPos + (rowHeight / 2));

        if (colIndex < visibleColumns.length - 1) {
          ctx.strokeStyle = '#e0e0e0';
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(xOffset + columnWidths[colIndex], yPos);
          ctx.lineTo(xOffset + columnWidths[colIndex], yPos + rowHeight);
          ctx.stroke();
        }

        xOffset += columnWidths[colIndex];
      });

      ctx.strokeStyle = '#e0e0e0';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, yPos + rowHeight);
      ctx.lineTo(tableWidth, yPos + rowHeight);
      ctx.stroke();
    });

    ctx.strokeStyle = '#0F49A0';
    ctx.lineWidth = 2;
    ctx.strokeRect(0, 0, tableWidth, tableHeight);

    return canvas;
  }

  private async dataUrlToCanvas(dataUrl: string): Promise<HTMLCanvasElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          resolve(canvas);
        } else {
          reject(new Error('Failed to get canvas context'));
        }
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = dataUrl;
    });
  }

  // Helper: Export table as image (for PNG/JPG BOTH export)
  private async exportTableToImage(
    format: string,
    fileName: string,
    scale: number,
    gridApi: GridApi
  ): Promise<void> {
    const tableElement = document.querySelector('.ag-theme-quartz') as HTMLElement;
    if (!tableElement) {
      throw new Error('Table element not found');
    }

    const canvas = await html2canvas(tableElement, {
      scale: scale,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff'
    });

    this.downloadCanvasAsImage(canvas, fileName, format);
  }
}